import { forwardRef } from "react";

// ── Palette (Trishul High School — navy + sky-blue + red accent) ──
const CREAM = "#fbf5e6";
const NAVY = "#1E3A6B";
const NAVY_DEEP = "#132749";
const SKY = "#93C5FD";
const SKY_SOFT = "#dbeafe";
const RED = "#DC2626";
const GOLD = "#f4b731";
const RIBBON_YELLOW = "#f4c02f";
const RIBBON_NAVY = NAVY;
const RIBBON_RED = RED;

const BUNTING = "/toppers/bunting.png";

/* ─── decorative SVGs (self-contained, no external assets) ─── */

function StarSvg({ size = 22, color = GOLD, stroke = "#0f2b60" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} stroke={stroke} strokeWidth="1.4" strokeLinejoin="round">
      <polygon points="12,2 15,9 22,9.5 16.5,14 18.5,21 12,17 5.5,21 7.5,14 2,9.5 9,9" />
    </svg>
  );
}

function InkPotAndQuill({ width = 96, height = 130 }) {
  return (
    <svg width={width} height={height} viewBox="0 0 96 130" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="ipBody" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#1b1b1b" />
          <stop offset="1" stopColor="#000" />
        </linearGradient>
        <linearGradient id="ipCap" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#2a2a2a" />
          <stop offset="1" stopColor="#0d0d0d" />
        </linearGradient>
        <linearGradient id="labelG" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#fff8e5" />
          <stop offset="1" stopColor="#f0e2b6" />
        </linearGradient>
      </defs>
      {/* Feather quill leaning against the pot */}
      <g transform="translate(58 6) rotate(24)">
        <path d="M 4 0 Q 20 24 12 66 Q 10 74 4 78 Q 0 74 -2 66 Q -6 24 4 0 z" fill="#DC2626" stroke="#7a1516" strokeWidth="1.1" />
        <path d="M 4 6 L 4 76" stroke="#7a1516" strokeWidth="0.8" opacity="0.7" />
        {[10, 20, 30, 40, 50, 60].map((y) => (
          <g key={y}>
            <path d={`M 4 ${y} L -3 ${y + 4}`} stroke="#7a1516" strokeWidth="0.6" opacity="0.7" />
            <path d={`M 4 ${y} L 11 ${y + 4}`} stroke="#7a1516" strokeWidth="0.6" opacity="0.7" />
          </g>
        ))}
        {/* nib */}
        <path d="M 4 78 L 0 92 L 8 92 z" fill="#c9ad4a" stroke="#7a5c00" strokeWidth="0.8" />
      </g>
      {/* Cap */}
      <rect x="24" y="26" width="48" height="12" rx="2.5" fill="url(#ipCap)" />
      <rect x="20" y="36" width="56" height="8" rx="2" fill="url(#ipCap)" />
      {/* Body */}
      <path d="M 16 44 h64 v66 a10 10 0 0 1 -10 10 h-44 a10 10 0 0 1 -10 -10 z" fill="url(#ipBody)" stroke="#000" strokeWidth="1.2" />
      {/* Highlight */}
      <rect x="22" y="50" width="4" height="58" rx="2" fill="#3a3a3a" opacity="0.9" />
      {/* Label */}
      <rect x="26" y="70" width="44" height="30" rx="4" fill="url(#labelG)" stroke="#7a5c00" strokeWidth="1.2" />
      <rect x="30" y="74" width="36" height="22" rx="2.5" fill="none" stroke="#c1a54a" strokeWidth="0.9" />
      <text x="48" y="92" textAnchor="middle" fontFamily="'Merriweather', serif" fontWeight="900" fontSize="16" fill="#1E3A6B">INK</text>
    </svg>
  );
}

function PenDeco({ width = 190, height = 180 }) {
  // A big, elegant fountain pen sitting diagonally in the corner — no hand,
  // so nothing overlaps the title text.
  return (
    <svg width={width} height={height} viewBox="0 0 190 180" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="penBody" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#3a5aa0" />
          <stop offset="0.6" stopColor="#1E3A6B" />
          <stop offset="1" stopColor="#0d1e3a" />
        </linearGradient>
        <linearGradient id="penGold" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#ffe17a" />
          <stop offset="1" stopColor="#c58c00" />
        </linearGradient>
      </defs>
      <g transform="translate(20 6) rotate(38 80 80)">
        {/* Cap end button */}
        <circle cx="10" cy="12" r="7" fill="#0d1e3a" />
        {/* Cap */}
        <rect x="14" y="4" width="46" height="18" rx="4" fill="url(#penBody)" stroke="#0a1c40" strokeWidth="1.1" />
        {/* Clip */}
        <path d="M 22 4 v 22 a3 3 0 0 0 6 0 v -22 z" fill="#dfa500" stroke="#7a5500" strokeWidth="0.8" />
        {/* Gold band */}
        <rect x="60" y="4" width="8" height="18" fill="url(#penGold)" stroke="#7a5500" strokeWidth="0.8" />
        {/* Barrel */}
        <path d="M 68 5 L 154 8 L 152 20 L 68 20 z" fill="url(#penBody)" stroke="#0a1c40" strokeWidth="1.1" />
        {/* Grip band */}
        <rect x="150" y="6" width="7" height="14" fill="url(#penGold)" stroke="#7a5500" strokeWidth="0.8" />
        {/* Grip section (tapered) */}
        <path d="M 157 6 L 176 9 L 174 18 L 157 20 z" fill="#2a2a2a" stroke="#000" strokeWidth="1" />
        {/* Nib */}
        <path d="M 176 9 L 195 12 L 193 15 L 174 18 z" fill="url(#penGold)" stroke="#7a5500" strokeWidth="1" />
        <path d="M 179 12.5 L 191 13.6" stroke="#7a5500" strokeWidth="0.8" />
        <circle cx="185" cy="13.5" r="1.4" fill="#7a5500" />
      </g>
      {/* Little ink stroke swoosh */}
      <path d="M 44 168 Q 74 148 118 156" fill="none" stroke={"#DC2626"} strokeWidth="3" strokeLinecap="round" opacity="0.75" />
      <path d="M 60 174 Q 88 160 128 168" fill="none" stroke={"#1E3A6B"} strokeWidth="2.5" strokeLinecap="round" opacity="0.55" />
    </svg>
  );
}

function BooksStack({ labels = ["Grammar", "Vocabulary", "Comprehension"] }) {
  const colors = ["#DC2626", "#f4b731", "#1E3A6B"];
  const textColors = ["#fff", "#1E3A6B", "#fff"];
  return (
    <svg width="230" height="170" viewBox="0 0 230 170" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="pageEdge" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#fff8e5" />
          <stop offset="1" stopColor="#d5bf7a" />
        </linearGradient>
      </defs>
      {labels.map((lbl, i) => {
        const y = 22 + i * 40;
        const rot = i === 0 ? -3 : i === 1 ? 2 : -1;
        const w = 190 - i * 6;
        const x = (230 - w) / 2;
        const c = colors[i % colors.length];
        const tc = textColors[i % textColors.length];
        return (
          <g key={i} transform={`rotate(${rot} 115 ${y + 16})`}>
            {/* page edges (right side) */}
            <rect x={x + 4} y={y + 3} width={w - 4} height={28} fill="url(#pageEdge)" stroke="#a89060" strokeWidth="0.8" />
            {[6, 11, 16, 21, 26].map((py) => (
              <line key={py} x1={x + 4} y1={y + py} x2={x + w - 2} y2={y + py} stroke="#c4a760" strokeWidth="0.35" opacity="0.7" />
            ))}
            {/* cover */}
            <rect x={x} y={y} width={w} height={32} rx="3" fill={c} stroke="#0d0d0d" strokeWidth="1.4" />
            {/* inner frame line */}
            <rect x={x + 5} y={y + 4} width={w - 10} height={24} rx="2" fill="none" stroke={tc === "#fff" ? "rgba(255,255,255,0.55)" : "rgba(30,58,107,0.55)"} strokeWidth="0.8" />
            {/* Title */}
            <text x={x + w / 2} y={y + 21} textAnchor="middle" fontFamily="'Merriweather', serif" fontWeight="800" fontSize="14" fill={tc} letterSpacing="0.4">{lbl}</text>
          </g>
        );
      })}
      {/* subtle floor shadow */}
      <ellipse cx="115" cy="160" rx="90" ry="6" fill="#000" opacity="0.14" />
    </svg>
  );
}

function StarburstBadge({ text, size = 172 }) {
  const cx = size / 2, cy = size / 2, r1 = size * 0.48, r2 = size * 0.36;
  const spikes = 22;
  const pts = [];
  for (let i = 0; i < spikes * 2; i++) {
    const ang = (Math.PI * i) / spikes - Math.PI / 2;
    const rr = i % 2 === 0 ? r1 : r2;
    pts.push(`${cx + rr * Math.cos(ang)},${cy + rr * Math.sin(ang)}`);
  }
  return (
    <div style={{ position: "relative", width: size, height: size, filter: "drop-shadow(0 4px 6px rgba(0,0,0,0.18))" }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <defs>
          <radialGradient id="burstFill" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0" stopColor="#f04141" />
            <stop offset="1" stopColor="#b71c1c" />
          </radialGradient>
        </defs>
        <polygon points={pts.join(" ")} fill="url(#burstFill)" stroke="#7a1516" strokeWidth="2" />
        <circle cx={cx} cy={cy} r={r2 * 0.96} fill="#fff3f3" stroke="#DC2626" strokeWidth="1.8" />
        <circle cx={cx} cy={cy} r={r2 * 0.86} fill="none" stroke="#DC2626" strokeWidth="0.6" strokeDasharray="2 2" opacity="0.7" />
      </svg>
      <div style={{
        position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
        textAlign: "center", padding: size * 0.22,
        fontFamily: "'Montserrat', sans-serif", fontWeight: 900, fontSize: 12, lineHeight: 1.2,
        color: "#7a1516", letterSpacing: 0.35, textTransform: "uppercase",
      }}>
        {text || "SHOW YOUR BEST WORK AND WIN!"}
      </div>
    </div>
  );
}

function Ribbon({ children, color = RIBBON_YELLOW, text = "#0f2b60", height = 44, tailColor = null }) {
  const tail = tailColor || (color === RIBBON_YELLOW ? "#c58c00" : color === RIBBON_NAVY ? "#0a1c40" : "#7a1516");
  return (
    <div style={{ position: "relative", display: "inline-flex", alignItems: "center", height, minWidth: 220 }}>
      {/* Left tail */}
      <svg width="24" height={height} viewBox={`0 0 24 ${height}`} style={{ marginRight: -1 }}>
        <path d={`M24 0 L4 ${height / 2} L24 ${height} z`} fill={tail} />
      </svg>
      <div style={{
        background: color, color: text, height,
        display: "flex", alignItems: "center", padding: "0 28px",
        fontFamily: "'Montserrat', sans-serif", fontWeight: 800, letterSpacing: 0.6,
        boxShadow: "0 2px 0 rgba(0,0,0,0.12)",
      }}>
        {children}
      </div>
      {/* Right tail */}
      <svg width="24" height={height} viewBox={`0 0 24 ${height}`} style={{ marginLeft: -1 }}>
        <path d={`M0 0 L20 ${height / 2} L0 ${height} z`} fill={tail} />
      </svg>
    </div>
  );
}

function initials(name) {
  const p = (name || "").trim().split(/\s+/).filter(Boolean);
  return ((p[0]?.[0] || "") + (p[1]?.[0] || "")).toUpperCase() || "?";
}

const CompetitionPoster = forwardRef(function CompetitionPoster(
  { data, photo = {} }, ref
) {
  const {
    profile = {},
    line1 = "KANNADA",
    line2 = "HANDWRITING",
    script = "Competition",
    tagline = "Good Handwriting is a Reflection of a Disciplined Mind and a Creative Soul.",
    month_label = "AUGUST MONTH WINNERS",
    congrats = "Congratulations to All Winners!",
    bottom_bar = "LET YOUR HANDWRITING SPEAK YOUR EXCELLENCE!",
    badge = "SHOW YOUR BEST HANDWRITING AND WIN APPRECIATION!",
    footer_line = "All the Students are Cordially Invited",
    photo_url = "",
    book_labels = ["Grammar", "Vocabulary", "Comprehension"],
    art_ink = "",       // optional user-uploaded override
    art_pen = "",       // optional user-uploaded override
    art_books = "",     // optional user-uploaded override
  } = data || {};

  const INK_SRC   = art_ink   || "/competition/ink.png";
  const PEN_SRC   = art_pen   || "/competition/hand-pen.png";
  const BOOKS_SRC = art_books || "/competition/books.png";

  const photoSrc = photo.override || photo_url || null;
  const scale = photo.scale ?? 1;
  const px = photo.x ?? 0;
  const py = photo.y ?? 0;
  const FW = 470, FH = 260;
  const IW = FW * scale, IH = FH * scale;
  const ILEFT = (FW - IW) / 2 + px;
  const ITOP = (FH - IH) / 2 + py;

  const cornerFlourish = (pos) => (
    <svg width="42" height="42" viewBox="0 0 42 42" style={{ position: "absolute", ...pos, pointerEvents: "none" }}>
      <path d="M6 36 Q6 14 28 14 M6 36 Q22 30 28 14 M6 36 L18 34 M6 36 L10 24" fill="none" stroke={NAVY} strokeWidth="1.7" strokeLinecap="round" />
      <circle cx="6" cy="36" r="2.2" fill={GOLD} stroke={NAVY} strokeWidth="1" />
    </svg>
  );

  return (
    <div
      ref={ref}
      data-testid="competition-poster"
      style={{
        width: 794, height: 1123, background: CREAM, position: "relative", overflow: "hidden",
        fontFamily: "'Montserrat', sans-serif", color: NAVY, boxSizing: "border-box",
        border: `3px solid ${NAVY}`, padding: 0,
      }}
    >
      {/* Inner decorative frame */}
      <div style={{ position: "absolute", inset: 10, border: `1.5px solid ${NAVY}55`, borderRadius: 8, pointerEvents: "none", zIndex: 6 }} />

      {/* ── Header (school profile) ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 18, padding: "22px 36px 6px" }}>
        {profile.logo ? (
          <img src={profile.logo} alt="logo" crossOrigin="anonymous" style={{ width: 96, height: 96, objectFit: "contain", flexShrink: 0 }} />
        ) : (
          <div style={{ width: 96, height: 96, borderRadius: "50%", background: "#fff", border: `3px solid ${NAVY}`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Merriweather, serif", color: NAVY, fontWeight: 900, fontSize: 22 }}>SCH</div>
        )}
        <div style={{ flex: 1, textAlign: "center", position: "relative" }}>
          <div style={{ fontFamily: "'Merriweather', serif", fontWeight: 900, color: NAVY, fontSize: 40, lineHeight: 1.05, letterSpacing: 1 }}>
            {profile.name || "SCHOOL NAME"}
          </div>
          {(profile.city || profile.address) ? (
            <div style={{ marginTop: 4, display: "inline-flex", alignItems: "center", gap: 6, color: NAVY }}>
              <span style={{ width: 22, height: 1.5, background: NAVY }} />
              <span style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, fontSize: 15, letterSpacing: 3 }}>
                {(profile.city || profile.address || "").toString().toUpperCase()}
              </span>
              <span style={{ width: 22, height: 1.5, background: NAVY }} />
            </div>
          ) : null}
        </div>
        {/* Reserve space matching the logo width so title stays visually centred */}
        <div style={{ width: 96, flexShrink: 0 }} />
      </div>

      {/* ── Decorative art (uses attached PNG crops, matching the reference sample) ── */}
      <img
        src={INK_SRC} alt="" crossOrigin="anonymous"
        style={{ position: "absolute", left: 22, top: 560, width: 92, height: "auto", zIndex: 3, filter: "drop-shadow(0 3px 4px rgba(0,0,0,0.28))" }}
      />
      <img
        src={PEN_SRC} alt="" crossOrigin="anonymous"
        style={{ position: "absolute", right: 6, top: 24, width: 170, height: "auto", zIndex: 3, filter: "drop-shadow(0 4px 6px rgba(0,0,0,0.22))" }}
      />

      {/* ── Competition Title ── */}
      <div style={{ marginTop: 8, textAlign: "center", position: "relative", zIndex: 2 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12 }}>
          <StarSvg size={20} />
          <div style={{ height: 1.5, width: 60, background: `${NAVY}66` }} />
          <StarSvg size={14} color="#3aa655" stroke="#0f2b60" />
        </div>
        <div style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 900, color: NAVY, fontSize: 76, lineHeight: 1, letterSpacing: 2, marginTop: 8, textShadow: "0 3px 0 rgba(15,43,96,0.08)" }}>
          {line1 || "KANNADA"}
        </div>
        <div style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 900, color: RED, fontSize: 82, lineHeight: 0.98, letterSpacing: 2, marginTop: 4, textShadow: "0 3px 0 rgba(198,40,40,0.20)" }}>
          {line2 || "HANDWRITING"}
        </div>
        <div style={{ fontFamily: "'Dancing Script', cursive", fontWeight: 700, color: NAVY, fontSize: 58, lineHeight: 1, marginTop: -6 }}>
          {script || "Competition"}
        </div>
      </div>

      {/* Yellow tagline ribbon */}
      <div style={{ textAlign: "center", marginTop: 18, position: "relative", zIndex: 4 }}>
        <Ribbon color={RIBBON_YELLOW} text={NAVY} height={56}>
          <div style={{ maxWidth: 500, fontFamily: "'Merriweather', serif", fontStyle: "italic", fontWeight: 700, fontSize: 15, lineHeight: 1.28, letterSpacing: 0.2, display: "flex", alignItems: "center", gap: 10 }}>
            <StarSvg size={14} color="#c62828" stroke="#7a1516" />
            <span style={{ textAlign: "center" }}>{tagline}</span>
            <StarSvg size={14} color="#c62828" stroke="#7a1516" />
          </div>
        </Ribbon>
      </div>

      {/* Navy month ribbon */}
      <div style={{ textAlign: "center", marginTop: 12, position: "relative", zIndex: 4 }}>
        <Ribbon color={RIBBON_NAVY} text="#fff" height={44}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, fontSize: 20, letterSpacing: 1 }}>
            <StarSvg size={14} color={GOLD} stroke="#7a5500" />
            {month_label || "MONTH WINNERS"}
            <StarSvg size={14} color={GOLD} stroke="#7a5500" />
          </div>
        </Ribbon>
      </div>

      {/* ── Photo frame ── */}
      <div style={{ margin: "16px auto 0", width: FW + 28, position: "relative", zIndex: 3 }}>
        <div style={{ position: "relative", width: FW + 28, padding: 14, background: "#fff", border: `2.5px solid ${NAVY}`, borderRadius: 6, boxShadow: "0 6px 14px rgba(15,43,96,0.16)" }}>
          {cornerFlourish({ top: -2, left: -2 })}
          {cornerFlourish({ top: -2, right: -2, transform: "scaleX(-1)" })}
          {cornerFlourish({ bottom: -2, left: -2, transform: "scaleY(-1)" })}
          {cornerFlourish({ bottom: -2, right: -2, transform: "scale(-1,-1)" })}
          <div style={{ position: "relative", width: FW, height: FH, overflow: "hidden", background: "#e9e4d5", borderRadius: 2 }}>
            {photoSrc ? (
              <div style={{ position: "absolute", left: ILEFT, top: ITOP, width: IW, height: IH }}>
                <img
                  src={photoSrc}
                  alt="Competition"
                  crossOrigin="anonymous"
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              </div>
            ) : (
              <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#8a7a4a", textAlign: "center", padding: 12 }}>
                <div style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 800, fontSize: 42, opacity: 0.7 }}>{initials(line1 || line2)}</div>
                <div style={{ marginTop: 8, fontSize: 13, fontWeight: 600, opacity: 0.85 }}>Upload the winners' photo</div>
              </div>
            )}
          </div>
        </div>

        {/* Red congrats ribbon overlapping the frame bottom */}
        <div style={{ position: "absolute", left: "50%", transform: "translateX(-50%)", bottom: -22, zIndex: 5 }}>
          <Ribbon color={RIBBON_RED} text="#fff" height={38}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 15, letterSpacing: 0.4, fontStyle: "italic", fontFamily: "'Dancing Script', cursive", fontWeight: 700 }}>
              <StarSvg size={12} color={GOLD} stroke="#7a5500" />
              {congrats}
              <StarSvg size={12} color={GOLD} stroke="#7a5500" />
            </div>
          </Ribbon>
        </div>
      </div>

      {/* ── Bottom composition: books · blue bar · badge ── */}
      <div style={{ position: "absolute", left: 0, right: 0, bottom: 78, padding: "0 26px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, zIndex: 3 }}>
        <div style={{ width: 240, flexShrink: 0, alignSelf: "flex-end" }}>
          <img
            src={BOOKS_SRC} alt="" crossOrigin="anonymous"
            style={{ width: "100%", height: "auto", display: "block", filter: "drop-shadow(0 4px 6px rgba(0,0,0,0.22))" }}
          />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{
            background: `linear-gradient(180deg, #274a86 0%, ${NAVY} 60%, ${NAVY_DEEP} 100%)`,
            color: "#fff", padding: "18px 20px", textAlign: "center",
            fontFamily: "Montserrat, sans-serif", fontWeight: 900, fontSize: 17, letterSpacing: 0.7,
            borderRadius: 10, boxShadow: "0 4px 0 rgba(0,0,0,0.18), 0 8px 20px rgba(30,58,107,0.28)",
            border: `1.5px solid ${NAVY_DEEP}`,
          }}>
            {bottom_bar}
          </div>
        </div>
        <div style={{ flexShrink: 0 }}>
          <StarburstBadge text={badge} size={172} />
        </div>
      </div>

      {/* ── Bottom cursive line ── */}
      <div style={{ position: "absolute", left: 0, right: 0, bottom: 20, textAlign: "center", zIndex: 3 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 14 }}>
          <span style={{ height: 1.5, width: 40, background: NAVY }} />
          <div style={{ fontFamily: "'Dancing Script', cursive", fontWeight: 700, color: NAVY, fontSize: 30, lineHeight: 1 }}>
            {footer_line}
          </div>
          <span style={{ height: 1.5, width: 40, background: NAVY }} />
        </div>
      </div>

      {/* Bunting accent (subtle, matches Class Toppers) */}
      <img src={BUNTING} alt="" style={{ position: "absolute", top: 8, left: 6, width: 220, opacity: 0.12, pointerEvents: "none", zIndex: 1 }} />
      <img src={BUNTING} alt="" style={{ position: "absolute", top: 8, right: 6, width: 220, opacity: 0.12, transform: "scaleX(-1)", pointerEvents: "none", zIndex: 1 }} />
    </div>
  );
});

export default CompetitionPoster;
